// InquiryActivityStepper.tsx
import * as React from 'react';
import { useEffect, useState } from 'react';
import { Box, Chip, Typography, Tooltip, Stepper, Step, StepLabel, IconButton, Collapse, } from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { darken } from '@mui/system';
import { CloseRounded, ErrorOutlineRounded } from '@mui/icons-material';
import { ProgressInquiryPageType } from '../../data/ProgressInquiryPageType';
import dayjs from "dayjs";

/**
 * InquiryActivityStepperのプロパティ型
 */
type Props = {
  record: ProgressInquiryPageType; // 表示する進捗照会データ
  orientation?: 'horizontal' | 'vertical'; // Stepperの表示方向
};

/**
 * 進捗照会アクティビティステッパーコンポーネント
 * - アクティビティの進捗状況をステップ形式で表示する
 * - props: record（進捗照会データ）, orientation（ステッパー方向）
 */
export default function InquiryActivityStepper({ record, orientation = 'horizontal' }: Props) {

  const [steps, setSteps] = useState<{ id: string; name: string; status: string; endAt: Date }[]>([]);
  const [expanded, setExpanded] = React.useState(false);
  // ステッパーの展開・折りたたみを切り替える
  const toggleExpand = (event: React.MouseEvent) => {
    event.stopPropagation();
    setExpanded(!expanded);
  };

  // recordが変更された際にアクティビティ一覧を実行順序でソートしてstepsに設定する
  useEffect(() => {
    if (record.activities && record.activities.length > 0) {
      const sortedSteps = [...record.activities]
        .sort((a, b) => a.order - b.order)
        .map((act) => ({
          id: act.id,
          name: act.activityName,
          status: act.activityStatus || "NotStarted",
          endAt: act.endAt,
        }));
      setSteps(sortedSteps);
    }
  }, [record]);

  const activeStep = steps.findIndex((step) => step.status === 'InProgress');

  const StepIcon = ({ stepStatus }: { stepStatus: string }) => {
    if (stepStatus === 'InProgress') {
      return <AutorenewIcon color="primary" fontSize="small" />;
    } else if (stepStatus === 'Completed') {
      return <TaskAltRoundedIcon color="success" fontSize="small" />;
    } else if (stepStatus === 'Failed') {
      return <CloseRounded color="error" fontSize="small" />;
    } else if (stepStatus === 'Error') {
      return <ErrorOutlineRounded color="warning" fontSize="small" />;
    } else {
      return <PauseCircleOutlineIcon  fontSize="small" 
              sx={{ 
                color: (theme) => darken(theme.palette.secondary.main, 0.3), 
              }}/>;
    }
  };

  if (!record) return <Typography>Loading...</Typography>;

  return (
    <Box sx={{ marginTop: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant="h3" sx={{ml:2, marginRight: 1 }}>
          申請進捗
        </Typography>
        <IconButton onClick={toggleExpand}>
          {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </IconButton>
      </Box>
      <Collapse in={expanded}>
    <Box>
      <Stepper activeStep={activeStep} orientation={orientation}>
        {steps.map((step) => (
          <Step key={step.id}>
            <StepLabel StepIconComponent={() => <StepIcon stepStatus={step.status} />}>{step.name} {(step.endAt && (step.status === 'Completed')) ? dayjs(step.endAt).format("YYYY/MM/DD HH:mm") : ""}</StepLabel>
          </Step>
        ))}
      </Stepper>
    </Box>
    </Collapse>
    </Box>
  );
}
